import type { VelocityView } from "../../shared/types/api";

/**
 * Velocity across completed sprints (§102).
 *
 * <p>Bars rather than a line, because these are discrete sprints rather than a continuous
 * quantity, and comparing bar heights against a shared baseline is easier than reading a line
 * between points that are not evenly spaced in time.
 *
 * <p>Committed sits behind completed on each bar. The gap between them is the thing worth looking
 * at: a team delivering 40 points a sprint against a 40-point commitment and one delivering 40
 * against 70 have the same velocity and completely different problems.
 *
 * <p>§102 says plainly that velocity must not become a per-person performance score. Nothing here
 * is attributable to a person, and nothing can be — the metrics are stored per sprint with no
 * attribution, so the data to build one does not exist.
 */
export function VelocityChart({ data }: { data: VelocityView }) {
  if (data.sprints.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-500">
        No completed sprints yet. Velocity appears once a sprint has been finished — there is
        nothing to average until then.
      </p>
    );
  }

  const ceiling = Math.max(
    ...data.sprints.map((sprint) =>
      Math.max(sprint.committedStoryPoints, sprint.completedStoryPoints),
    ),
    1,
  );

  return (
    <div>
      <div className="flex items-end gap-3 overflow-x-auto pb-2" style={{ height: 180 }}>
        {data.sprints.map((sprint) => {
          const committedHeight = (sprint.committedStoryPoints / ceiling) * 140;
          const completedHeight = (sprint.completedStoryPoints / ceiling) * 140;
          const shortfall = sprint.committedStoryPoints - sprint.completedStoryPoints;

          return (
            <div
              key={sprint.sprintId}
              className="flex min-w-16 flex-1 flex-col items-center gap-1"
              title={`${sprint.name}: committed ${sprint.committedStoryPoints}, completed ${sprint.completedStoryPoints}`}
            >
              <span className="tnum text-xs font-medium text-ink-900">
                {sprint.completedStoryPoints}
              </span>

              <div className="relative flex w-full justify-center" style={{ height: 140 }}>
                {/* Committed, behind — the promise the bar in front is measured against. */}
                <span
                  className="absolute bottom-0 w-8 rounded-t bg-ink-200"
                  style={{ height: committedHeight }}
                  aria-hidden
                />
                <span
                  className="absolute bottom-0 w-8 rounded-t bg-brand-600"
                  style={{ height: completedHeight }}
                  aria-hidden
                />
              </div>

              <span className="w-full truncate text-center text-[10px] text-ink-500">
                {sprint.name}
              </span>
              {shortfall > 0 && (
                <span className="text-[10px] text-amber-600">−{shortfall}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap gap-4 text-xs text-ink-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-3 rounded-sm bg-brand-600" aria-hidden />
          Completed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-3 rounded-sm bg-ink-200" aria-hidden />
          Committed
        </span>
      </div>
    </div>
  );
}
